import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Empty,
  Typography,
  Tag,
  Divider,
  Select,
  Space,
} from "antd";
import {
  SolutionOutlined,
  ProjectOutlined,
  ApartmentOutlined,
  TeamOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { getSessions } from "../../services/sessionService";
import { MdOutlineCalendarToday } from "react-icons/md";

const { Text } = Typography;
const { Option } = Select;

const PartnersTab = ({ institution }) => {
  const { partners = [] } = institution;

  const [selectedPartner, setSelectedPartner] = useState("All");
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("All");

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(data.data);
    } catch (error) {
      toast.error("Failed to load sessions");
    }
  };

  const uniquePartners = useMemo(() => {
    const list = partners.map((p) => p.name).filter(Boolean);
    return ["All", ...Array.from(new Set(list))];
  }, [partners]);

  const uniqueIndustries = useMemo(() => {
    const list = partners.map((p) => p.partner_industry).filter(Boolean);
    return ["All", ...Array.from(new Set(list))];
  }, [partners]);

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      const matchesPartner =
        selectedPartner === "All" || p.name === selectedPartner;
      const matchesIndustry =
        selectedIndustry === "All" || p.partner_industry === selectedIndustry;
      const matchSession =
        selectedSessionId === "All" || p.session?.id === selectedSessionId;
      return matchesPartner && matchesIndustry && matchSession;
    });
  }, [partners, selectedPartner, selectedIndustry, selectedSessionId]);

  return (
    <div className="pt-5 pb-6">
      <Space className="mb-6 flex-wrap" size="large">
        <Select
          value={selectedPartner}
          onChange={setSelectedPartner}
          style={{ minWidth: 200 }}
          placeholder="Filter by Partner"
          suffixIcon={<FilterOutlined />}
        >
          {uniquePartners.map((partner) => (
            <Option key={partner} value={partner}>
              {partner}
            </Option>
          ))}
        </Select>

        <Select
          value={selectedIndustry}
          onChange={setSelectedIndustry}
          style={{ minWidth: 200 }}
          placeholder="Filter by Industry"
          suffixIcon={<FilterOutlined />}
        >
          {uniqueIndustries.map((industry) => (
            <Option key={industry} value={industry}>
              {industry}
            </Option>
          ))}
        </Select>

        <Select
          value={selectedSessionId}
          onChange={setSelectedSessionId}
          className="min-w-[250px]"
          size="middle"
        >
          <Option value="All">All Years</Option>
          {sessions.map((session) => (
            <Option key={session.id} value={session.id}>
              {session.name}
            </Option>
          ))}
        </Select>
      </Space>

      {filteredPartners.length === 0 ? (
        <Empty
          description="No partners match the selected filters."
          className="my-12"
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filteredPartners.map((partner) => (
            <Col key={partner.id} xs={24} sm={12} md={6}>
              <Card
                className="border border-gray-200! hover:shadow-sm transition-all"
                title={
                  <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <TeamOutlined className="text-blue-500" />
                    {partner.name}
                  </div>
                }
              >
                <div className="text-sm text-gray-700 space-y-2">
                  {partner.session?.name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <MdOutlineCalendarToday className="text-gray-400" />
                      <Text>
                        <strong>Year:</strong> {partner.session.name}
                      </Text>
                    </div>
                  )}
                  {partner.partner_type && (
                    <p>
                      <SolutionOutlined className="mr-1 text-indigo-500" />
                      <strong>Type:</strong>{" "}
                      <Tag color="blue">{partner.partner_type.name}</Tag>
                    </p>
                  )}

                  {partner.partner_industry && (
                    <p>
                      <ApartmentOutlined className="mr-1 text-green-600" />
                      <strong>Industry:</strong>{" "}
                      <Tag color="green">{partner.partner_industry}</Tag>
                    </p>
                  )}

                  {partner.project && (
                    <p>
                      <ProjectOutlined className="mr-1 text-purple-600" />
                      <strong>Project:</strong>{" "}
                      <Tag color="purple">{partner.project}</Tag>
                    </p>
                  )}

                  {partner.description && (
                    <>
                      <Divider className="my-2" />
                      <Text className="block leading-relaxed text-gray-600">
                        {partner.description}
                      </Text>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default PartnersTab;
